import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import TextInput from 'components/Inputs/TextInput';
import Dropdown from 'components/Fields/FieldInput/Dropdown';
import { FIELD_TYPES } from 'utility/constants';
import {
  countriesToCountryOptions,
  countriesToStateOptions,
} from '../../helpers';

const noKeyListeners = () => ({});

export const BusinessAddressFields = ({
  formData,
  countries,
  handleInputChange,
  validationProps,
  getKeyListenersProps = noKeyListeners,
  singleColumn = false,
}) => {
  const { t } = useTranslation();

  const statesList = useMemo(
    () => countriesToStateOptions(countries, formData.country),
    [countries, formData.country]
  );

  const countriesList = useMemo(
    () => countriesToCountryOptions(countries),
    [countries]
  );

  const menuProps = singleColumn
    ? { menuInline: true, menuPlacement: 'bottom' }
    : { menuPlacement: 'top' };

  const streetAddress = (
    <TextInput
      className="spaced-input"
      label={t('Street Address')}
      placeholder={t('Enter Street Address')}
      value={formData.street_address}
      onChange={(value, ev, err) =>
        handleInputChange('street_address', value, err)
      }
      {...validationProps('street_address')}
      required
      {...getKeyListenersProps('street-address')}
      data-qa-id="business-address-street"
    />
  );

  const country = (
    <Dropdown
      className="spaced-input"
      label={t('Country')}
      placeholder={t('Enter Country')}
      options={countriesList}
      value={formData.country}
      {...menuProps}
      onChange={(value, ev, err) => {
        handleInputChange('country', value, err, {
          state: null,
        });
      }}
      {...validationProps('country')}
      required
      field={{
        fieldType: FIELD_TYPES.Dropdown,
        options: countriesList,
      }}
      menuLeftButton={null}
      menuRightButton={null}
      {...getKeyListenersProps('country')}
      data-qa-id="business-address-country"
    />
  );

  const state = statesList.length ? (
    <Dropdown
      className="spaced-input preserve-size"
      label={t('State/Province')}
      placeholder={t('Enter State/Province')}
      options={statesList}
      value={formData.state}
      {...menuProps}
      onChange={(value, ev, err) => {
        handleInputChange('state', value, err);
      }}
      {...validationProps('state')}
      required
      field={{
        fieldType: FIELD_TYPES.Dropdown,
        options: statesList,
      }}
      menuLeftButton={null}
      menuRightButton={null}
      {...getKeyListenersProps('state-province')}
      data-qa-id="business-address-state"
    />
  ) : (
    <TextInput
      className="spaced-input preserve-size"
      label={t('State/Province')}
      placeholder={t('Enter State/Province')}
      value={formData.state}
      onChange={(value, ev, err) => handleInputChange('state', value, err)}
      {...getKeyListenersProps('state-province')}
      data-qa-id="business-address-state"
    />
  );

  const city = (
    <TextInput
      className="spaced-input"
      label={t('City')}
      placeholder={t('Enter City')}
      value={formData.city}
      onChange={(value, ev, err) => {
        handleInputChange('city', value, err);
      }}
      {...validationProps('city')}
      required
      {...getKeyListenersProps('city')}
      data-qa-id="business-address-city"
    />
  );

  const postalCode = (
    <TextInput
      className="spaced-input"
      label={t('Zip/Postal Code')}
      placeholder={t('Enter/Postal Zip Code')}
      value={formData.postal_code}
      onChange={(value, ev, err) => {
        handleInputChange('postal_code', value, err);
      }}
      required
      {...validationProps('postal_code')}
      {...getKeyListenersProps('zip-postal-code')}
      data-qa-id="business-address-postal-code"
    />
  );

  if (singleColumn) {
    return (
      <div className="flex flex-col gap-[15px]">
        {streetAddress}
        {country}
        {postalCode}
        {city}
        {state}
      </div>
    );
  }

  return (
    <>
      <Row>
        <Col>{streetAddress}</Col>
      </Row>
      <Row>
        <Col>{country}</Col>
        <Col>{state}</Col>
      </Row>
      <Row>
        <Col>{city}</Col>
        <Col>{postalCode}</Col>
      </Row>
    </>
  );
};
